import {
  ContentType,
  SourceType,
  WebsitePageStatus,
} from '@kanbu/schema/enums';

import type {
  KBContentRow,
  KBDocumentData,
  KBFolderData,
} from '../components/kbTypes';

export type KBRowActionContext = {
  isChatMode: boolean;
  isManagedSource: boolean;
};

export type KBDestructiveAction = 'delete' | 'exclude';

export type KBCutState = {
  enabled: boolean;
  disabledTooltip?: string;
};

type KBCutLabels = {
  globalKbCutDisabledTooltip: string;
  webHierarchyCutDisabledTooltip: string;
};

export function isTrackedWebsiteRootFolder(folder: KBFolderData): boolean {
  return !!folder.sourceUri && !folder.websitePathPrefix;
}

export function isWebsiteSubpathFolder(folder: KBFolderData): boolean {
  return !!folder.websitePathPrefix;
}

export function isRegularFolder(
  folder: Pick<KBFolderData, 'sourceType' | 'sourceUri' | 'websitePathPrefix'>,
): boolean {
  return (
    (!folder.sourceType || folder.sourceType === SourceType.Upload) &&
    !folder.sourceUri &&
    !folder.websitePathPrefix
  );
}

function getPopulatedDocumentFolder(
  document: KBDocumentData,
): Pick<KBFolderData, 'sourceType' | 'sourceUri' | 'websitePathPrefix'> | null {
  const folder = document.folder;

  if (!folder || typeof folder === 'string') {
    return null;
  }

  return folder;
}

function isDocumentUnderWebsiteTree(
  document: KBDocumentData,
  context: KBRowActionContext,
): boolean {
  if (context.isManagedSource) {
    return true;
  }

  const folder = getPopulatedDocumentFolder(document);

  return !!(folder && !isRegularFolder(folder));
}

function isFolderUnderWebsiteTree(
  folder: KBFolderData,
  context: KBRowActionContext,
): boolean {
  if (context.isManagedSource) {
    return true;
  }

  return !isRegularFolder(folder);
}

function getFolderDestructiveAction(
  folder: KBFolderData,
): KBDestructiveAction | null {
  if (isTrackedWebsiteRootFolder(folder) || isRegularFolder(folder)) {
    return 'delete';
  }

  if (isWebsiteSubpathFolder(folder)) {
    return 'exclude';
  }

  return null;
}

function getDocumentDestructiveAction(
  document: KBDocumentData,
  context: KBRowActionContext,
): KBDestructiveAction | null {
  if (document.type === ContentType.Document) {
    return 'delete';
  }

  if (
    document.type !== ContentType.Website &&
    document.type !== ContentType.ExternalDocument
  ) {
    return null;
  }

  if (document.websitePage?.status === WebsitePageStatus.Removed) {
    return 'delete';
  }

  return isDocumentUnderWebsiteTree(document, context) ? 'exclude' : 'delete';
}

export function getRowDestructiveAction(
  row: KBContentRow,
  context: KBRowActionContext,
): KBDestructiveAction | null {
  if (row.rowType === 'page') {
    if (row.data.status === WebsitePageStatus.Removed) return 'delete';

    return row.data.status === WebsitePageStatus.Existing ? 'exclude' : null;
  }

  if (row.rowType === 'folder') {
    return getFolderDestructiveAction(row.data);
  }

  return getDocumentDestructiveAction(row.data, context);
}

export function canExcludeRow(
  row: KBContentRow,
  context: KBRowActionContext,
): boolean {
  return (
    getRowDestructiveAction(row, context) === 'exclude' ||
    (row.rowType === 'document' &&
      row.data.websitePage?.status === WebsitePageStatus.Removed)
  );
}

export function getCurrentFolderDestructiveAction(
  folder: KBFolderData,
): KBDestructiveAction | null {
  return getFolderDestructiveAction(folder);
}

export function getKBCutState(
  context: KBRowActionContext,
  labels: KBCutLabels,
): KBCutState {
  if (context.isChatMode) {
    return {
      enabled: false,
      disabledTooltip: labels.globalKbCutDisabledTooltip,
    };
  }

  if (context.isManagedSource) {
    return {
      enabled: false,
      disabledTooltip: labels.webHierarchyCutDisabledTooltip,
    };
  }

  return { enabled: true };
}

export function getKBRowCutState(
  row: KBContentRow,
  context: KBRowActionContext,
  labels: KBCutLabels,
): KBCutState {
  const baseState = getKBCutState(context, labels);

  if (!baseState.enabled) {
    return baseState;
  }

  if (row.rowType === 'page') {
    return { enabled: false };
  }

  if (
    (row.rowType === 'document' &&
      isDocumentUnderWebsiteTree(row.data, context)) ||
    (row.rowType === 'folder' && isFolderUnderWebsiteTree(row.data, context))
  ) {
    return {
      enabled: false,
      disabledTooltip: labels.webHierarchyCutDisabledTooltip,
    };
  }

  return { enabled: true };
}

export function getKBSelectionCutState(
  rows: KBContentRow[],
  ids: Iterable<string>,
  context: KBRowActionContext,
  labels: KBCutLabels,
): KBCutState {
  const baseState = getKBCutState(context, labels);

  if (!baseState.enabled) {
    return baseState;
  }

  for (const id of ids) {
    const row = rows.find(item => item.id === id);

    if (!row) {
      continue;
    }

    const rowCutState = getKBRowCutState(row, context, labels);

    if (!rowCutState.enabled) {
      return rowCutState;
    }
  }

  return { enabled: true };
}

export function selectionHasExcludeTarget(
  rows: KBContentRow[],
  ids: Iterable<string>,
  context: KBRowActionContext,
): boolean {
  for (const id of ids) {
    const row = rows.find(r => r.id === id);

    if (row && canExcludeRow(row, context)) {
      return true;
    }
  }

  return false;
}

export function selectionHasDeleteTarget(
  rows: KBContentRow[],
  ids: Iterable<string>,
  context: KBRowActionContext,
): boolean {
  for (const id of ids) {
    const row = rows.find(r => r.id === id);

    if (row && getRowDestructiveAction(row, context) === 'delete') {
      return true;
    }
  }

  return false;
}

export function collectExcludeTargets(
  rows: KBContentRow[],
  ids: Iterable<string>,
  context: KBRowActionContext,
): { pageIds: string[]; documentIds: string[]; folderIds: string[] } {
  const pageIds: string[] = [];
  const documentIds: string[] = [];
  const folderIds: string[] = [];

  for (const id of ids) {
    const row = rows.find(r => r.id === id);

    if (!row || !canExcludeRow(row, context)) {
      continue;
    }

    switch (row.rowType) {
      case 'page':
        pageIds.push(id);

        break;

      case 'document':
        documentIds.push(id);

        break;

      case 'folder':
        folderIds.push(id);

        break;

      // No default
    }
  }

  return { pageIds, documentIds, folderIds };
}

export function collectDeleteTargets(
  rows: KBContentRow[],
  ids: Iterable<string>,
  context: KBRowActionContext,
): { pageIds: string[]; documentIds: string[]; folderIds: string[] } {
  const pageIds: string[] = [];
  const documentIds: string[] = [];
  const folderIds: string[] = [];

  for (const id of ids) {
    const row = rows.find(r => r.id === id);

    if (!row || getRowDestructiveAction(row, context) !== 'delete') {
      continue;
    }

    switch (row.rowType) {
      case 'page':
        pageIds.push(id);

        break;

      case 'document':
        if (row.data.websitePage?.status === WebsitePageStatus.Removed) {
          pageIds.push(row.data.websitePage.id);
        } else {
          documentIds.push(id);
        }

        break;

      case 'folder':
        folderIds.push(id);

        break;

      // No default
    }
  }

  return { pageIds, documentIds, folderIds };
}
